"use client";

import { caseMaterialLabel } from "@/utils/case-material";

import SectionLayout from "@/components/ui/SectionLayout";
import React, { useEffect } from "react";
import { CaseSectionProps } from "../model";
import { getDefaultCmsSection } from "@/utils/helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faChevronDown,
  faFile,
  faHandHoldingDollar,
  faHome,
} from "@fortawesome/sharp-light-svg-icons";
import SomeSharingLinks from "@/components/ui/SomeSharingLinks";
import { usePathname } from "next/navigation";
import PropertyFacts from "@/components/ui/PropertyFacts";
import Card from "@/components/ui/Card";
import { motion } from "motion/react";
import Link from "next/link";
import RippleCtaButton from "@/components/ui/RippleCtaButton";
import ClassicCasePresentation from "./ClassicCasePresentation";
import { ownSectionBgColor, ownSectionTextColor } from "@/utils/theme-helper";

/** A page picked in the CMS is stored as its slug without the leading slash, which would make the link relative to the
 *  case's own address. */
function assessmentHref(slug?: string) {
  const target = slug || "/saelgbolig";
  return `${/^([/#]|[a-z][a-z\d+.-]*:)/i.test(target) ? target : `/${target}`}#assessment-form`;
}

/** What the folded description fades into: the page's colour, the section's own, or, over a picture or Farveforløb og
 *  billede på mobil's layer (drawn above the section's colour), nothing, the text itself fading out. */
const FOLD_FADE = {
  page: "data-[collapsed=true]:bg-gradient-to-b from-transparent to-[var(--page-bg-color)]",
  section:
    "data-[collapsed=true]:bg-gradient-to-b from-transparent to-[var(--section-bg-color)]",
  mask: "",
};

/** The facts box, its buttons and the share icons. Their column is stretched to the row, which the folded description
 *  makes taller, so the column's own height would add the heading above the description on every resize. */
function infoAreaHeight(column: HTMLElement | null) {
  const last = column?.lastElementChild;
  return column && last
    ? last.getBoundingClientRect().bottom - column.getBoundingClientRect().top
    : 0;
}

const DefaultCasePresentationSection: React.FC<
  CaseSectionProps<{
    card: { bgColor: string; textColor: string };
    somelinks: string[];
    someStyle: "square" | "default";
    assesmentFormSlug?: string;
    hideAssessmentFormButton?: boolean;
    layoutName?: string;
    showStandardFinancing?: boolean;
  }>
> = ({ cmsSection, caseData, pageCtx, cmsData }) => {
  const pathname = usePathname();
  const fullUrl = `${pageCtx.baseUrl}${pathname}`;
  const encodedFullUrl = encodeURIComponent(fullUrl);

  const [collapsed, setCollapsed] = React.useState(true);
  const [showExpand, setShowExpand] = React.useState(true);
  const [height, setHeight] = React.useState(0);
  const contentSectionRef = React.useRef<HTMLDivElement>(null);
  const menuSectionRef = React.useRef<HTMLDivElement>(null);

  const isSecondaryLayout = cmsData.layout?.name === "secondary";
  const fade: keyof typeof FOLD_FADE = cmsSection.ui?.background
    ? "mask"
    : (cmsSection.lookAttributes?.background ?? ownSectionBgColor(cmsSection))
      ? "section"
      : (cmsSection.specifics as { bgImage?: string }).bgImage ||
          cmsSection.ui?.bgImage
        ? "mask"
        : "page";

  useEffect(() => {
    const updateHeight = () => {
      const contentSection = contentSectionRef.current;
      const collapsedHeight = window.matchMedia("(min-width: 768px)").matches
        ? infoAreaHeight(menuSectionRef.current)
        : 100;

      let totalHeight = 0;
      if (contentSection) {
        Array.from(contentSection.children).forEach((child) => {
          const childElement = child as HTMLElement;
          const style = window.getComputedStyle(childElement);
          const marginTop = parseFloat(style.marginTop);
          const marginBottom = parseFloat(style.marginBottom);
          totalHeight += childElement.clientHeight + marginTop + marginBottom;
        });
      }

      setShowExpand(totalHeight > collapsedHeight);
      setHeight(collapsedHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);
  return (
    <SectionLayout
      cmsSection={getDefaultCmsSection(cmsSection)}
      ownHeight="content"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 md:gap-10">
          <div className="overflow-hidden relative transition-all duration-300 ease-in-out flex-1">
            <div className=" flex flex-1 flex-col">
              {isSecondaryLayout ? (
                <>
                  <h1 className="homepage-text-lg primary-font md:homepage-pb-lg">
                    {caseData.marketingTexts?.headline}
                  </h1>
                </>
              ) : (
                <>
                  <h1 className="text-header-level-3">
                    {caseData.advertisementAddressOrDisplayName}
                  </h1>
                  <h2 className="homepage-text-lg primary-font">
                    {caseData.marketingTexts?.headline}
                  </h2>
                </>
              )}
            </div>
            <motion.div className="flex flex-col">
              <div className="relative flex-col flex flex-1 overflow-hidden">
                <motion.div
                  ref={contentSectionRef}
                  id="contentSection"
                  animate={{ height: collapsed ? height : "auto" }}
                  style={{ height: `${height}px` }}
                  className={`styled-description homepage-text-sm${fade === "mask" && collapsed && showExpand ? " [mask-image:linear-gradient(to_bottom,#000_calc(100%-4rem),transparent)]" : ""}`}
                  dangerouslySetInnerHTML={{
                    __html: caseData.marketingTexts?.description ?? <div></div>,
                  }}
                ></motion.div>
                {showExpand && (
                  <button
                    id="expand-text-btn"
                    data-collapsed={collapsed}
                    onClick={() => setCollapsed(!collapsed)}
                    className={`data-[collapsed=true]:absolute flex justify-center items-end data-[collapsed=false]:pt-4 data-[collapsed=true]:pt-10 to-70%  ${FOLD_FADE[fade]} z-10 inset-x-0 bottom-0`}
                  >
                    <FontAwesomeIcon
                      data-collapsed={collapsed}
                      icon={faChevronDown}
                      className="size-5 data-[collapsed=false]:rotate-180"
                    />
                  </button>
                )}
              </div>
            </motion.div>
          </div>
          <div
            ref={menuSectionRef}
            id="menuSection"
            className="w-full md:w-96 gap-4 flex flex-col flex-shrink-0"
          >
            <Card>
              <div
                style={{
                  backgroundColor: cmsSection.specifics?.card?.bgColor,
                  color: cmsSection.specifics?.card?.textColor,
                }}
              >
                {isSecondaryLayout ? (
                  <h2 className="p-4 pb-0 flex justify-between homepage-text-md gap-2 items-center primary-font">
                    <div>{caseData.advertisementAddressOrDisplayName}</div>
                  </h2>
                ) : (
                  <>
                    <div className="p-4 pb-0 flex justify-between homepage-text-lg gap-2 items-center">
                      <div className="text-ellipsis whitespace-nowrap overflow-hidden primary-font">
                        {caseData?.estateType}
                      </div>
                      <div className="whitespace-nowrap primary-font">
                        {caseData.priceInfo}
                      </div>
                    </div>
                  </>
                )}
                <PropertyFacts
                  caseData={caseData}
                  showPrice={isSecondaryLayout}
                  subdomain={cmsData.subdomain}
                  showStandardFinancing={
                    cmsSection.specifics.showStandardFinancing ?? true
                  }
                />
              </div>
            </Card>

            <div className="grid grid-cols-1 gap-2">
              {caseData.isActive && caseData.documents?.length > 0 && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "documents" },
                      }),
                    );
                    e.preventDefault();
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon className="size-5" icon={faFile} />
                    {caseMaterialLabel(caseData, "Hent salgsmateriale")}
                  </span>
                </RippleCtaButton>
              )}
              {caseData.isActive && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    e.preventDefault();
                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "viewing" },
                      }),
                    );
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon className="size-5" icon={faCalendarDays} />
                    <span>Bestil fremvisning</span>
                  </span>
                </RippleCtaButton>
              )}
              {!cmsSection.specifics.hideAssessmentFormButton && (
                <Link
                  href={assessmentHref(cmsSection.specifics?.assesmentFormSlug)}
                >
                  <RippleCtaButton
                    config={{
                      borderColor: "transparent",
                    }}
                    className="w-full justify-start"
                  >
                    <span className="flex items-center gap-2">
                      <FontAwesomeIcon className="size-5" icon={faHome} />
                      <span>Bestil vurdering</span>
                    </span>
                  </RippleCtaButton>
                </Link>
              )}
              {caseData.isActive && caseData.canSendBid && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    e.preventDefault();
                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "bid" },
                      }),
                    );
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon
                      className="size-5"
                      icon={faHandHoldingDollar}
                    />
                    <span>Giv et bud</span>
                  </span>
                </RippleCtaButton>
              )}
            </div>

            <div className="md:w-96 flex justify-end items-start">
              <SomeSharingLinks
                somelinks={
                  cmsSection.specifics.somelinks ?? [
                    "linkedin",
                    "instagram",
                    "twitter",
                    "facebook",
                    "mail",
                  ]
                }
                style={cmsSection.specifics.someStyle ?? "default"}
                encodedFullUrl={encodedFullUrl}
                caseData={caseData}
                followsSectionText={!!ownSectionTextColor(cmsSection)}
              />
            </div>
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};

export default function CasePresentationSection(
  props: React.ComponentProps<typeof DefaultCasePresentationSection> & {
    cmsSection: CaseSectionProps<{ presentation?: "classic" }>["cmsSection"];
  },
) {
  return props.cmsSection.specifics.presentation === "classic" ? (
    <ClassicCasePresentation {...props} />
  ) : (
    <DefaultCasePresentationSection {...props} />
  );
}
